import React, { useState } from "react";
import getApiUrl from "../../../config/api";

export default function UserSyncPanel({
    language,
    currentUser,
    onSyncComplete,
    hasRole,
}) {
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const syncAllUsers = async () => {
        setSyncing(true);
        setSyncResult(null);

        try {
            const token = await currentUser.getIdToken();
            // console.log(
            //     "Attempting to sync users with token:",
            //     token.substring(0, 20) + "..."
            // );

            const response = await fetch(
                `${getApiUrl()}/api/admin/sync-all-users`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                },
            );

            // console.log("Response status:", response.status);
            // console.log("Response headers:", response.headers);

            // Check if response is actually JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const textResponse = await response.text();
                console.error(
                    "Non-JSON response received:",
                    textResponse.substring(0, 200),
                );
                alert(
                    `Server error: Expected JSON but received ${contentType}. Check console for details.`,
                );
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                console.error("HTTP error:", response.status, data);
                alert(
                    `HTTP Error ${response.status}: ${
                        data.error || "Unknown error"
                    }`,
                );
                return;
            }

            if (data.success) {
                setSyncResult(data.result);
                if (onSyncComplete) {
                    onSyncComplete();
                }
                // console.log("Sync completed successfully:", data.result);
                alert(
                    `Sync completed: ${data.result.syncedCount} users synced successfully.`,
                );
            } else {
                console.error("Sync failed with error:", data.error);
                alert(`Sync failed: ${data.error}`);
            }
        } catch (error) {
            console.error("Sync failed:", error);
            alert(`Sync failed: ${error.message}. Check console for details.`);
        } finally {
            setSyncing(false);
        }
    };

    if (hasRole("users.sync") === false) {
        return (
            <div className="text-center flex flex-col items-center p-4 font-semibold">
                <p className="text-gray-600">
                    {language === "zh"
                        ? "您沒有權限存取此部分。"
                        : "You do not have permission to access this section."}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900">
                {language === "en" ? "User Synchronization" : "用戶同步"}
            </h2>
            {/* Sync Controls */}
            <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === "en"
                        ? "Firebase Auth → Firestore Sync"
                        : "Firebase 認證 → Firestore 同步"}
                </h3>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-blue-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                {language === "en"
                                    ? "This will copy all Firebase Auth users to Firestore for enhanced querying and data management."
                                    : "這將把所有 Firebase 認證用戶複製到 Firestore，以便進行增強的查詢和數據管理。"}
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    onClick={syncAllUsers}
                    disabled={syncing}
                    className={`px-6 py-3 rounded-lg font-medium ${
                        syncing
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                >
                    {syncing ? (
                        <span className="flex items-center gap-2">
                            <span className="flex gap-1">
                                <span className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"></span>
                                <span
                                    className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                ></span>
                                <span
                                    className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                ></span>
                            </span>
                            <span className="text-orange-300">
                                {language === "en" ? "Syncing..." : "同步中..."}
                            </span>
                        </span>
                    ) : language === "en" ? (
                        "Sync All Users"
                    ) : (
                        "同步所有用戶"
                    )}
                </button>
            </div>

            {/* Sync Results */}
            {syncResult && (
                <div className="bg-white rounded-lg border p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        {language === "en" ? "Sync Results" : "同步結果"}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-600">
                                    {syncResult.syncedCount}
                                </p>
                                <p className="text-sm text-green-700">
                                    {language === "en"
                                        ? "Synced Successfully"
                                        : "同步成功"}
                                </p>
                            </div>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-red-600">
                                    {syncResult.failedCount}
                                </p>
                                <p className="text-sm text-red-700">
                                    {language === "en" ? "Failed" : "失敗"}
                                </p>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">
                                    {syncResult.totalUsers}
                                </p>
                                <p className="text-sm text-blue-700">
                                    {language === "en"
                                        ? "Total Processed"
                                        : "總處理數"}
                                </p>
                            </div>
                        </div>
                    </div>

                    {syncResult.failedUsers &&
                        syncResult.failedUsers.length > 0 && (
                            <div className="mt-4">
                                <h4 className="font-medium text-red-600 mb-2">
                                    {language === "en"
                                        ? "Failed Users:"
                                        : "失敗的用戶："}
                                </h4>
                                <div className="space-y-1">
                                    {syncResult.failedUsers.map(
                                        (user, index) => (
                                            <p
                                                key={index}
                                                className="text-sm text-red-600"
                                            >
                                                • {user.email}: {user.error}
                                            </p>
                                        ),
                                    )}
                                </div>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}
